/**
 * BVA Email Templates
 *
 * 5 branded HTML email templates for marketing campaigns.
 * All templates use inline CSS for email client compatibility.
 * Supports {{firstName}} merge tag (replaced per-recipient at send time).
 */

export interface TemplateParams {
  heading: string;
  body: string;
  ctaText?: string;
  ctaUrl?: string;
  preheader?: string;
}

export interface TemplateResult {
  html: string;
  plainText: string;
}

export interface TemplateDefinition {
  id: string;
  name: string;
  description: string;
  render: (params: TemplateParams) => TemplateResult;
}

const BRAND = {
  navy: '#1C3D5A',
  navyLight: '#2c5aa0',
  gold: '#D4A537',
  gray: '#666666',
  lightGray: '#f8f9fa',
  white: '#ffffff',
  text: '#333333',
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function bodyToHtml(body: string): string {
  return body
    .split('\n\n')
    .filter(p => p.trim())
    .map(p => `<p style="margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: ${BRAND.text};">${escapeHtml(p.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('\n');
}

function bodyToPlainText(body: string): string {
  return body.trim();
}

function ctaButton(text: string, url: string, bgColor: string = BRAND.navyLight): string {
  if (!text || !url) return '';
  return `
    <table role="presentation" cellpadding="0" cellspacing="0" style="margin: 28px auto;">
      <tr>
        <td style="background-color: ${bgColor}; border-radius: 6px;">
          <a href="${escapeHtml(url)}" target="_blank"
             style="display: inline-block; padding: 14px 32px; color: ${BRAND.white}; text-decoration: none; font-size: 16px; font-weight: bold; font-family: Arial, sans-serif;">
            ${escapeHtml(text)}
          </a>
        </td>
      </tr>
    </table>`;
}

function footer(): string {
  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top: 32px; border-top: 1px solid #e0e0e0; padding-top: 20px;">
      <tr>
        <td style="text-align: center; font-size: 12px; color: #999999; font-family: Arial, sans-serif;">
          <p style="margin: 0 0 8px 0;">&copy; ${new Date().getFullYear()} Bank Vault Academy. All rights reserved.</p>
          <p style="margin: 0;">
            <a href="https://bankvaultacademy.org" style="color: ${BRAND.navyLight}; text-decoration: underline;">bankvaultacademy.org</a>
          </p>
        </td>
      </tr>
    </table>`;
}

function header(title?: string): string {
  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px;">
      <tr>
        <td style="text-align: center; padding: 24px 0 16px 0; border-bottom: 3px solid ${BRAND.gold};">
          <span style="font-family: Georgia, serif; font-size: 22px; font-weight: bold; color: ${BRAND.navy}; letter-spacing: 1px;">
            BANK VAULT ACADEMY
          </span>
          ${title ? `<br><span style="font-size: 13px; color: ${BRAND.gray}; font-family: Arial, sans-serif;">${escapeHtml(title)}</span>` : ''}
        </td>
      </tr>
    </table>`;
}

function wrapEmail(content: string, preheader?: string): string {
  const preheaderHtml = preheader
    ? `<div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;">${escapeHtml(preheader)}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bank Vault Academy</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, sans-serif;">
  ${preheaderHtml}
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
    <tr>
      <td style="padding: 24px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; background-color: ${BRAND.white}; border-radius: 8px; overflow: hidden;">
          <tr>
            <td style="padding: 32px;">
              ${content}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

function makePlainText(heading: string, body: string, ctaText?: string, ctaUrl?: string): string {
  let text = `${heading}\n${'='.repeat(heading.length)}\n\n${bodyToPlainText(body)}`;
  if (ctaText && ctaUrl) {
    text += `\n\n${ctaText}: ${ctaUrl}`;
  }
  text += `\n\n---\n© ${new Date().getFullYear()} Bank Vault Academy\nhttps://bankvaultacademy.org`;
  return text;
}

// --- Template Definitions ---

const newsletter: TemplateDefinition = {
  id: 'newsletter',
  name: 'Newsletter',
  description: 'Monthly updates, content roundups, and news',
  render: (params) => {
    const content = `
      ${header('Newsletter')}
      <h1 style="margin: 0 0 20px 0; font-size: 26px; color: ${BRAND.navy}; font-family: Georgia, serif; font-weight: bold;">
        ${escapeHtml(params.heading)}
      </h1>
      ${bodyToHtml(params.body)}
      ${ctaButton(params.ctaText || '', params.ctaUrl || '')}
      ${footer()}`;

    return {
      html: wrapEmail(content, params.preheader),
      plainText: makePlainText(params.heading, params.body, params.ctaText, params.ctaUrl),
    };
  },
};

const announcement: TemplateDefinition = {
  id: 'announcement',
  name: 'Announcement',
  description: 'New features, launches, and important updates',
  render: (params) => {
    const content = `
      ${header()}
      <div style="text-align: center; padding: 20px 0;">
        <h1 style="margin: 0 0 24px 0; font-size: 30px; color: ${BRAND.navy}; font-family: Georgia, serif; font-weight: bold;">
          ${escapeHtml(params.heading)}
        </h1>
        <div style="width: 60px; height: 3px; background-color: ${BRAND.gold}; margin: 0 auto 24px auto;"></div>
      </div>
      ${bodyToHtml(params.body)}
      ${ctaButton(params.ctaText || '', params.ctaUrl || '')}
      ${footer()}`;

    return {
      html: wrapEmail(content, params.preheader),
      plainText: makePlainText(params.heading, params.body, params.ctaText, params.ctaUrl),
    };
  },
};

const promotion: TemplateDefinition = {
  id: 'promotion',
  name: 'Promotion',
  description: 'Sales, discounts, free trials, and special offers',
  render: (params) => {
    const content = `
      ${header()}
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom: 24px;">
        <tr>
          <td style="background-color: ${BRAND.navy}; border-radius: 8px; padding: 28px; text-align: center;">
            <h1 style="margin: 0; font-size: 28px; color: ${BRAND.white}; font-family: Georgia, serif; font-weight: bold;">
              ${escapeHtml(params.heading)}
            </h1>
          </td>
        </tr>
      </table>
      ${bodyToHtml(params.body)}
      ${ctaButton(params.ctaText || '', params.ctaUrl || '', BRAND.gold)}
      ${footer()}`;

    return {
      html: wrapEmail(content, params.preheader),
      plainText: makePlainText(params.heading, params.body, params.ctaText, params.ctaUrl),
    };
  },
};

const welcome: TemplateDefinition = {
  id: 'welcome',
  name: 'Welcome',
  description: 'Onboarding, new signups, and getting started',
  render: (params) => {
    const content = `
      ${header()}
      <h1 style="margin: 0 0 20px 0; font-size: 26px; color: ${BRAND.navy}; font-family: Georgia, serif; font-weight: bold;">
        ${escapeHtml(params.heading)}
      </h1>
      ${bodyToHtml(params.body)}
      ${ctaButton(params.ctaText || 'Get Started', params.ctaUrl || 'https://bankvaultacademy.org')}
      ${footer()}`;

    return {
      html: wrapEmail(content, params.preheader),
      plainText: makePlainText(params.heading, params.body, params.ctaText || 'Get Started', params.ctaUrl || 'https://bankvaultacademy.org'),
    };
  },
};

const minimal: TemplateDefinition = {
  id: 'minimal',
  name: 'Minimal',
  description: 'Simple messages, reminders, and quick notes',
  render: (params) => {
    const content = `
      <h2 style="margin: 0 0 20px 0; font-size: 22px; color: ${BRAND.navy}; font-family: Georgia, serif; font-weight: bold;">
        ${escapeHtml(params.heading)}
      </h2>
      ${bodyToHtml(params.body)}
      ${ctaButton(params.ctaText || '', params.ctaUrl || '')}
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top: 32px; border-top: 1px solid #e0e0e0; padding-top: 16px;">
        <tr>
          <td style="font-size: 12px; color: #999999; font-family: Arial, sans-serif;">
            <p style="margin: 0;">Bank Vault Academy &middot; <a href="https://bankvaultacademy.org" style="color: ${BRAND.navyLight};">bankvaultacademy.org</a></p>
          </td>
        </tr>
      </table>`;

    return {
      html: wrapEmail(content, params.preheader),
      plainText: makePlainText(params.heading, params.body, params.ctaText, params.ctaUrl),
    };
  },
};

export const EMAIL_TEMPLATES: TemplateDefinition[] = [
  newsletter,
  announcement,
  promotion,
  welcome,
  minimal,
];

export function getTemplate(id: string): TemplateDefinition | undefined {
  return EMAIL_TEMPLATES.find(t => t.id === id);
}

export function renderTemplate(templateId: string, params: TemplateParams): TemplateResult {
  const template = getTemplate(templateId);
  if (!template) {
    throw new Error(`Unknown template: ${templateId}`);
  }
  return template.render(params);
}
